'use client';

import { useState, FormEvent, ChangeEvent } from 'react';

interface ContactFormState {
  name: string;
  email: string;
  phone: string;
  desiredPosition: string;
  message: string;
  consent: boolean;
}

const customStyles = `
  .btn-accent { background: var(--accent); color: #052e16; }
  .btn-accent:hover { filter: brightness(0.95); }
  .card { background: rgba(255,255,255,0.08); backdrop-filter: blur(8px); }
  .chip { background: var(--chip); }
  .simple-card { background: rgba(255,255,255,0.08); padding: 1rem; border-radius: 0.75rem; }
  input:not([type="checkbox"]), textarea {
    background-color: rgba(255, 255, 255, 0.9);
  }
`;

const SolutionsSeekers = () => {
  const [contactForm, setContactForm] = useState<ContactFormState>({
    name: '',
    email: '',
    phone: '',
    desiredPosition: '',
    message: '',
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    const newValue = type === 'checkbox' ? target.checked : value;

    setContactForm({ ...contactForm, [name]: newValue });
  };

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (!contactForm.consent) {
      setSubmitStatus('error');
      console.error('Consent is required to submit the form.');
      setIsSubmitting(false);
      return;
    }

    const { consent, ...dataToSend } = contactForm;

    const payload = {
      formName: 'Решения для соискателей',
      ...dataToSend,
    };

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/candidates-requests`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: payload }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({
          name: '',
          email: '',
          phone: '',
          desiredPosition: '',
          message: '',
          consent: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 antialiased"
      style={{ '--accent': '#FFA500', '--brand': '#003087', '--chip': 'rgba(255,255,255,.14)' } as React.CSSProperties}
    >
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />

      <header
        className="px-6 pt-32 pb-12 md:pt-40 md:pb-20 md:h-[505px]"
        style={{
          background: `
            linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)),
            radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)`,
        }}
      >
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap items-center gap-3 text-sm">
            <a href="#services" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Сервисы
            </a>
            <a href="#how" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Как это работает
            </a>
            <a href="#jobs" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Вакансии
            </a>
            <a href="#reviews" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Отзывы
            </a>
            <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              FAQ
            </a>
            <a href="#contact" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Контакты
            </a>
          </nav>

          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
            Решения для соискателей
          </h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Поможем быстро найти подходящую работу: прокачаем резюме и профиль, подготовим к интервью, подскажем, где
            вырастить навыки и как договориться о достойной оффер‑ставке.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#contact" className="btn-accent px-6 py-3 rounded-xl font-semibold">
              Бесплатный аудит резюме
            </a>
            <a
              href="#services"
              className="px-4 py-3 rounded-xl font-semibold inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base"
            >
              Посмотреть сервисы
            </a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-10">
        <section id="services" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Что мы делаем для кандидатов
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Карьерная консультация</h3>
              <p className="text-sm text-gray-200 mb-3">
                Определим цель, карту роста, подходящие направления и уровень зарплаты.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Диагностика сильных сторон</li>
                <li>• План перехода и обучения</li>
                <li>• Рынок и вилка дохода</li>
              </ul>
              <a
                href="#contact"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Получить консультацию
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Резюме и профиль</h3>
              <p className="text-sm text-gray-200 mb-3">
                Соберём сильное резюме и обновим профиль (HH/LinkedIn/портфолио).
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Акценты под роль</li>
                <li>• Ключевые слова ATS</li>
                <li>• Чек‑лист ошибок</li>
              </ul>
              <a
                href="#contact"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Прокачать резюме
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Подготовка к интервью</h3>
              <p className="text-sm text-gray-200 mb-3">
                Прогоним «через вопросы», отработаем кейсы и переговоры по офферу.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Мок‑интервью 60 минут</li>
                <li>• Домашние задания/кейсы</li>
                <li>• Зарплатные переговоры</li>
              </ul>
              <a
                href="#contact"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Записаться
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Подбор вакансий</h3>
              <p className="text-sm text-gray-200 mb-3">
                Составим список релевантных позиций и сделаем рефералы к работодателям.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• 10–20 таргет‑вакансий</li>
                <li>• Реферал и сопровождение</li>
                <li>• Отчёт по откликам</li>
              </ul>
              <a
                href="#contact"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Получить подборку
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Портфолио и кейсы</h3>
              <p className="text-sm text-gray-200 mb-3">
                Оформим проекты и результаты в понятные кейсы для работодателя.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Структура & визуал</li>
                <li>• Метрики и impact</li>
                <li>• GitHub/Behance/Slides</li>
              </ul>
              <a
                href="#contact"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Помочь с портфолио
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Навыки и обучение</h3>
              <p className="text-sm text-gray-200 mb-3">
                Подскажем курсы и практику под целевую роль. Дадим задачи и проверки.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Дорожная карта навыков</li>
                <li>• Практические задания</li>
                <li>• Контроль прогресса</li>
              </ul>
              <a
                href="#contact"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Подбор обучения
              </a>
            </article>
          </div>
          <p className="text-sm text-gray-300 mt-4">
            Стоимость и пакеты — на странице{' '}
            <a className="underline" href="/pricing">
              «Цены»
            </a>
            .
          </p>
        </section>

        <section id="how" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Как это работает
          </h2>
          <ol className="grid gap-6 md:grid-cols-3">
            <li className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-1">1. Бриф</div>
              <p className="text-sm text-gray-200">
                Короткая анкета и звонок 20–30 мин — фиксируем цель и ожидания.
              </p>
            </li>
            <li className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-1">2. Аудит резюме</div>
              <p className="text-sm text-gray-200">
                Чек‑лист, правки, ключевые слова под ATS, подбор релевантных примеров.
              </p>
            </li>
            <li className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-1">3. Подбор позиций</div>
              <p className="text-sm text-gray-200">
                Список вакансий + стратегия откликов и рефералов, лента отслеживания.
              </p>
            </li>
            <li className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-1">4. Мок‑интервью</div>
              <p className="text-sm text-gray-200">
                Отработка вопросов, кейсов и презентации проекта/портфолио.
              </p>
            </li>
            <li className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-1">5. Переговоры</div>
              <p className="text-sm text-gray-200">
                Помощь с оффером: вилка, бонусы, корректная аргументация.
              </p>
            </li>
            <li className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-1">6. Поддержка</div>
              <p className="text-sm text-gray-200">
                30 дней после выхода: адаптация, цели первого месяца.
              </p>
            </li>
          </ol>
        </section>

        <section id="jobs" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Актуальные вакансии
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            <article className="card rounded-2xl p-6">
              <div className="text-sm text-emerald-300 mb-1">
                Полная занятость · Гибрид
              </div>
              <h3 className="text-xl font-semibold">
                Менеджер по работе с клиентами
              </h3>
              <p className="text-sm text-gray-200 mt-1">от 90 000 ₽ · Москва</p>
              <ul className="text-sm text-gray-200 mt-3 space-y-1">
                <li>• CRM, обработка заявок, апсейл</li>
                <li>• Опыт 1–3 года</li>
              </ul>
              <a
                href="#contact"
                className="mt-4 inline-block px-4 py-2 rounded-lg font-semibold bg-white/10 hover:bg-white/20"
              >
                Откликнуться
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <div className="text-sm text-emerald-300 mb-1">
                Стажировка · Remote
              </div>
              <h3 className="text-xl font-semibold">Junior маркетолог</h3>
              <p className="text-sm text-gray-200 mt-1">30 000–50 000 ₽ · Россия</p>
              <ul className="text-sm text-gray-200 mt-3 space-y-1">
                <li>• Аналитика, контент, соцсети</li>
                <li>• 0–1 год опыта</li>
              </ul>
              <a
                href="#contact"
                className="mt-4 inline-block px-4 py-2 rounded-lg font-semibold bg-white/10 hover:bg-white/20"
              >
                Откликнуться
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <div className="text-sm text-emerald-300 mb-1">
                Полная занятость · Remote
              </div>
              <h3 className="text-xl font-semibold">QA‑инженер</h3>
              <p className="text-sm text-gray-200 mt-1">120 000–160 000 ₽ · Россия</p>
              <ul className="text-sm text-gray-200 mt-3 space-y-1">
                <li>• Тест‑кейсы, авто‑тесты, API</li>
                <li>• Опыт 2+ года</li>
              </ul>
              <a
                href="#contact"
                className="mt-4 inline-block px-4 py-2 rounded-lg font-semibold bg-white/10 hover:bg-white/20"
              >
                Откликнуться
              </a>
            </article>
          </div>
          <p className="text-sm text-gray-300 mt-4">
            Хотите закрытый пул вакансий? Напишите в форме ниже — пришлём релевантные позиции.
          </p>
        </section>

        <section id="reviews" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Отзывы
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            <blockquote className="card rounded-2xl p-6 text-sm text-gray-200">
              «С INTERIM подготовилась к собеседованию за неделю, оффер вырос на 18%. Спасибо за разбор кейса и
              переговоры.»
              <br />
              <span className="text-gray-300 text-xs">— Алина, продакт‑менеджер</span>
            </blockquote>
            <blockquote className="card rounded-2xl p-6 text-sm text-gray-200">
              «Собрали сильное резюме и портфолио. Уже через 10 дней вышел на работу, помогли с адаптацией.»
              <br />
              <span className="text-gray-300 text-xs">— Михаил, дизайнер</span>
            </blockquote>
            <blockquote className="card rounded-2xl p-6 text-sm text-gray-200">
              «Очень полезный бриф и чек‑лист. Поняла, куда расти и что подтянуть в ближайший месяц.»
              <br />
              <span className="text-gray-300 text-xs">— Оксана, HR‑специалист</span>
            </blockquote>
          </div>
        </section>

        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Частые вопросы
          </h2>
          <div className="space-y-3">
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">
                Сколько длится сопровождение?
              </summary>
              <p className="mt-2 text-sm text-gray-200">
                Базово — до выхода на работу + поддержка в течение 30 дней. Можно продлить.
              </p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">
                Нужен ли опыт, чтобы начать?
              </summary>
              <p className="mt-2 text-sm text-gray-200">
                Нет. Мы подскажем стажировки, задачи и курсы для начального портфолио.
              </p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">
                Поможете с переговорами по зарплате?
              </summary>
              <p className="mt-2 text-sm text-gray-200">
                Да. Рассчитаем рыночную вилку, подготовим аргументацию и сценарий переговоров.
              </p>
            </details>
          </div>
        </section>

        <section id="contact" className="py-12 scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Получить бесплатный аудит резюме
          </h2>
          <form onSubmit={handleContactSubmit} className="simple-card p-6 grid gap-4 md:grid-cols-2">
            <input
              name="name"
              value={contactForm.name}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Имя"
              required
            />
            <input
              name="email"
              value={contactForm.email}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="E‑mail"
              type="email"
              required
            />
            <input
              name="phone"
              value={contactForm.phone}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Телефон"
              type="tel"
              required
            />
            <input
              name="desiredPosition"
              value={contactForm.desiredPosition}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Желаемая должность"
              required
            />
            <textarea
              name="message"
              value={contactForm.message}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Кратко опишите опыт и цель"
              required
            ></textarea>

            <div className="md:col-span-2 flex items-center space-x-2">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={contactForm.consent}
                onChange={handleInputChange}
                className="h-4 w-4 rounded text-[#FFA500] bg-white/10 border-gray-600 focus:ring-[#FFA500] cursor-pointer"
                required
              />
              <label htmlFor="consent" className="text-xs text-gray-400 cursor-pointer">
                Я соглашаюсь с{' '}
                <a
                  href="/policy"
                  className="underline text-[#FFA500] hover:text-[#FFB84D] transition-colors duration-200"
                >
                  условиями обработки персональных данных
                </a>
              </label>
            </div>

            <button
              type="submit"
              className="btn-accent rounded-md py-2 font-semibold md:col-span-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </button>

            {submitStatus === 'success' && (
              <div className="md:col-span-2 text-center text-sm text-green-300">
                Ваша заявка успешно отправлена!
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                Ошибка при отправке. Пожалуйста, попробуйте снова.
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
};

export default SolutionsSeekers;